import { SafePromise, safeMap } from '@benzinga/safe-await';
import { RestfulClient } from '@benzinga/session';
import { Session } from '@benzinga/session';
import { ShopEnvironment } from './environment';
import { IPrecheckoutRequest, ILineItemPrice, IPrecheckoutResponse } from './entities';
import { IngressCard, IngressCheckoutSubscription, IngressCoupon, IngressProduct } from './ingress';

export interface ILineItem {
  amount: number;
  currency: string;
  date_created: string;
  feature_name: string;
  feature_slug: string;
  feature_uuid: string;
  localized_price: string;
  uuid: string;
}

enum LineItemProps {
  featureSlug = 'feature_slug',
}

export interface CheckoutPost {
  coupon?: string;
  line_items: ILineItem[LineItemProps.featureSlug][];
  plan: string;
  token?: string;
  will_activate_on?: string;
  source?: string;
  subscription?: string;
}

export interface CartBail {
  planSlug: string;
  submitted: boolean;
}

export class ShopRestful extends RestfulClient {
  constructor(session: Session) {
    super(session.getEnvironment(ShopEnvironment).url, session, { authorizationSession: true, 'x-device-key': true });
  }

  public getProduct = (productName: string): SafePromise<IngressProduct> => {
    const url = this.URL(`products/${productName}/`);
    return this.get(url, { allowsAnonymousAuth: true });
  };

  public getProducts = (): SafePromise<IngressProduct[]> => {
    const url = this.URL('products/');
    return this.get(url);
  };

  public preCheckout = (body: IPrecheckoutRequest): SafePromise<IPrecheckoutResponse> => {
    const url = this.URL('precheckout/');
    return this.post(url, body);
  };

  public upgradePlan = (
    selected_card: string,
    selected_plan: string,
    subscription_uuid: string,
  ): SafePromise<ILineItemPrice> => {
    const url = this.URL(`subscriptions/${subscription_uuid}/upgrade`, {
      card: selected_card,
      new_plan: selected_plan,
    });
    return this.post(url);
  };

  public getCouponsCode = (code: string): SafePromise<IngressCoupon> => {
    const url = this.URL(`coupons/${code}`);
    return this.get(url);
  };

  public postCheckout = (body: CheckoutPost): SafePromise<IngressCheckoutSubscription> => {
    const url = this.URL('checkout/');
    if (body.token === '') {
      delete body.token;
    }
    return this.post(url, body);
  };

  public cartBail = (body: CartBail): SafePromise<undefined> => {
    const url = this.URL('cartbail/ping/', body);
    return this.get(url);
  };

  public getCreditCards = (): SafePromise<IngressCard[]> => {
    const url = this.URL('cards/');
    return this.get(url);
  };

  public addCreditCard = (token: string): SafePromise<IngressCard> => {
    const url = this.URL('cards/');
    return this.post(url, { token });
  };

  public getCreditCard = (id: string): SafePromise<IngressCard> => {
    const url = this.URL(`cards/${id}`);
    return this.get(url);
  };

  public deleteCreditCard = (id: string): SafePromise<undefined> => {
    const url = this.URL(`cards/${id}`);
    return safeMap(this.delete(url), () => undefined);
  };
}
